import { computed, inject, Injectable } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { FeatureSet } from '../../models/feature.model';
import { CasbinPermissionsService } from './casbin-permissions.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  private permissionsService = inject(CasbinPermissionsService);
  private readonly router = inject(Router);

  hasPermission(featureSet?: FeatureSet, feature?: string, entity?: string) {
    return this.permissionsService.hasPermission(featureSet, feature, entity);
  }

  canView(featureSet?: FeatureSet, entity?: string) {
    return this.permissionsService.canView(featureSet, entity);
  }

  /**
   * Returns a deep clone of routes suitable for navigation.
   * Strips functions (guards/resolvers) automatically.
   */
  getNavigableRoutes(): Routes {
    // Read the live router config (equivalent to the static `routes`) rather
    // than importing app.routes — importing it created a module cycle
    // (app.routes → authGuard → FeatureService → app.routes) that broke the
    // test bundle. Stringify/Parse is safe here because we explicitly want to
    // lose the functions for a simple nav menu.
    return JSON.parse(JSON.stringify(this.router.config));
  }

  // Used as the primary navigation menu.
  // This is a computed property so that it can be filtered based on the user's permissions and selected region.
  navigableItems = computed(() => {
    const permissionSet = this.permissionsService.permissionSet();
    const filter = this.routePermissionFilter(permissionSet.features);
    return this.filterRoutes(this.getNavigableRoutes(), filter);
  });

  routePermissionFilter = (userFeatures: string[]) => (route: Route) => {
    // 1. Is the route explicitly hidden from navigation?
    const isHidden = route.data && route.data['hideFromNavigation'];
    if (isHidden) return false;

    // 2. Is the route a redirect?
    const isRedirect = !!route.redirectTo;
    // Redirects are not visible.
    if (isRedirect) return false;

    // 3. Does the route have an assigned feature-set?
    const feature: string | undefined = route.data?.['featureSet'];
    // Any public route is permissible and all routes are permissible for Admins.
    if (feature === 'public' || userFeatures?.includes('*')) return true;
    // If the route has a feature set, check if the user has that feature set.
    return feature ? userFeatures?.includes(feature) || false : false;
  };

  // Traverses the route tree recursively and runs the condition on each route.
  filterRoutes(routes: Route[], condition: (route: Route) => boolean): Route[] {
    return routes.filter((route) => {
      const isVisible = condition(route);

      if (route.children && route.children.length > 0) {
        route.children = this.filterRoutes(route.children, condition);
        // If the route no longer has any children
        // AND has no component of its own, it's not visible.'
        if (route.children.length === 0 && !route.component) {
          return false;
        }
      }

      const hasVisibleChildren = route.children && route.children.length > 0;

      return isVisible || hasVisibleChildren;
    });
  }
}
